/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';

import PhenixWebSocket from 'services/websocket.provider';
import {authenticate, clearCredentials, IAuthResponse} from 'services/auth.service';

import {updateUser, logoutUser, ILogoutUser} from './user';
import {transformToPortalError} from 'utility/error-handler';

export const WS_CONNECT = 'WS_CONNECT' as const;
export const WS_CONNECTED = 'WS_CONNECTED' as const;
export const WS_STATUS_UPDATE = 'WS_STATUS_UPDATE' as const;
export const WS_CONNECTION_UPDATE = 'WS_CONNECTION_UPDATE' as const;
export const WS_ERROR = 'WS_ERROR' as const;
export const WS_ROLLED_CONNECTIONS_DISCONNECT = 'WS_ROLLED_CONNECTIONS_DISCONNECT' as const;
export const WS_ROLLED_CONNECTION_REMOVE = 'WS_ROLLED_CONNECTION_REMOVE' as const;
export const WS_LOGOUT = 'WS_LOGOUT' as const;

export const wsStatus = {
  connected: 'connected',
  reconnecting: 'reconnecting',
  authenticated: 'authenticated',
  disconnected: 'disconnected'
};

interface IWSConnectActions {
  type: typeof WS_CONNECT;
  callback?: (value: unknown) => void;
}
export const wsConnect = (callback?: (value: unknown) => void): IWSConnectActions => ({
  type: WS_CONNECT,
  callback
});

export interface IWSConnectedActions {
  type: typeof WS_CONNECTED;
  socket: PhenixWebSocket;
  callback?: (value?: unknown) => void;
}
export const wsConnected = (socket: PhenixWebSocket, callback?: (value?: unknown) => void): IWSConnectedActions => ({
  type: WS_CONNECTED,
  socket,
  callback
});

interface IWSUpdateActions {
  type: typeof WS_STATUS_UPDATE;
  socket: PhenixWebSocket;
  status: string;
}
export const wsUpdateStatus = (socket: PhenixWebSocket, status: string): IWSUpdateActions => ({
  type: WS_STATUS_UPDATE,
  status,
  socket
});

interface IWSErrorActions {
  type: typeof WS_ERROR;
  error: string | null;
}
export const wsError = (error: string | null): IWSErrorActions => ({
  type: WS_ERROR,
  error
});

interface IWSDisconnectRolledSocketsActions {
  type: typeof WS_ROLLED_CONNECTIONS_DISCONNECT;
}
export const wsDisconnectRolledSockets = (): IWSDisconnectRolledSocketsActions => ({type: WS_ROLLED_CONNECTIONS_DISCONNECT});

interface IWSRemoveRolledSocketActions {
  type: typeof WS_ROLLED_CONNECTION_REMOVE;
  socket: PhenixWebSocket;
}
export const wsRemoveRolledSocket = (
  socket: PhenixWebSocket
): IWSRemoveRolledSocketActions => ({
  type: WS_ROLLED_CONNECTION_REMOVE,
  socket
});

interface IWSLogoutActiveSocketActions {
  type: typeof WS_LOGOUT;
}
export const wsLogoutActiveSocket = (): IWSLogoutActiveSocketActions => ({type: WS_LOGOUT});

export const initiateWebsocketConnection = () => (dispatch: Dispatch<IWSConnectActions>): Promise<unknown> => // TODO: return type of function needs to be discovered more
  new Promise(resolve => {
    dispatch(wsConnect(resolve));
  });

export const attemptLogin = (applicationId: string, secret: string) => async(
  // TODO: Refactor initiateWebsocketConnection. Make it do not pass the resolve callback to receive a socket. Provide dispatch with the proper type after that
  dispatch,
  socket: PhenixWebSocket
): Promise<{response: IAuthResponse; applicationId: string}> => {
  try {
    if (!socket || socket.isDisconnected()) {
      socket = await dispatch(initiateWebsocketConnection());
    }

    const response = await authenticate(socket, applicationId, secret);

    await dispatch(updateUser(applicationId));
    await dispatch(wsUpdateStatus(socket, wsStatus.authenticated));

    return response;
  } catch (e) {
    const portalError = transformToPortalError(e);

    throw portalError;
  }
};

export const logout = () => {
  return async(dispatch: Dispatch<ILogoutUser | IWSLogoutActiveSocketActions>): Promise<void> => {
    try {
      await clearCredentials();
      dispatch(logoutUser());
      dispatch(wsLogoutActiveSocket());

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };
};

export type WebsocketActions =
  | IWSConnectActions
  | IWSConnectedActions
  | IWSUpdateActions
  | IWSErrorActions
  | IWSDisconnectRolledSocketsActions
  | IWSRemoveRolledSocketActions
  | IWSLogoutActiveSocketActions;